// Keynote scene primitives. Each is a lightweight layout — no airline-specific
// content. Slides compose these with their own headline + copy + imagery.
import { useTheme } from "../context/ThemeContext";

export const SceneEyebrow = ({ children, color }) => {
  const { isDark } = useTheme();
  return (
    <div className="k-eyebrow" style={{ color: color || (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)") }}>
      {children}
    </div>
  );
};

export const SceneHeadline = ({ children, accent }) => {
  const { isDark } = useTheme();
  return (
    <h2
      className={`k-h2 max-w-[16ch] mt-2 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}
    >
      {children}
      {accent && <span className="block" style={{ color: accent }}>{accent}</span>}
    </h2>
  );
};

export const SceneLead = ({ children }) => {
  const { isDark } = useTheme();
  return (
    <p className={`k-lead mt-6 max-w-[54ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
      {children}
    </p>
  );
};

/**
 * ScenePillar — the presentation composition for a pillar page.
 *  { eyebrow, headline, accentLine, lead, visual, outcomes: [{ label, value }] }
 *
 * Layout: title + lead across the top; the interactive visual sits directly
 * below at full width, with the KPI outcomes to its right on wide screens.
 */
export const ScenePillar = ({
  eyebrow,
  headline,
  accentLine,
  accent = "#F2A900",
  lead,
  visual,
  outcomes = [],
  children,
}) => {
  const { isDark } = useTheme();
  return (
    <div>
      <div className="max-w-[860px]">
        <div className="k-eyebrow" style={{ color: accent }}>{eyebrow}</div>
        <h2 className={`k-h2 mt-3 max-w-[14ch] ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
          {headline}
          {accentLine && <span className="block" style={{ color: accent }}>{accentLine}</span>}
        </h2>
        {lead && (
          <p className={`k-lead mt-5 max-w-[54ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
            {lead}
          </p>
        )}
        {children}
      </div>

      <div className="mt-12 grid grid-cols-12 gap-6 lg:gap-8 items-start">
        <div className="col-span-12 lg:col-span-8">{visual}</div>
        <div className="col-span-12 lg:col-span-4">
          {outcomes.length > 0 && (
            <dl className="grid gap-5" style={{ gridTemplateColumns: outcomes.length > 2 ? `repeat(${outcomes.length}, minmax(0, 1fr))` : "1fr" }}>
              {outcomes.map((o, i) => (
                <div key={i} className={`border-t pt-4 ${isDark ? "border-white/15" : "border-black/12"}`}>
                  <dt className={`k-eyebrow ${isDark ? "text-white/45" : "text-black/45"}`}>{o.label}</dt>
                  <dd className={`mt-1.5 text-[26px] font-semibold tracking-tight ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
                    {o.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * A quiet "detail table" surface — used in place of the previous card grids
 * when a slide must list features/services.
 */
export const DetailList = ({ items }) => {
  const { isDark } = useTheme();
  return (
    <ul className={`divide-y ${isDark ? "divide-white/10" : "divide-black/10"}`}>
      {items.map((it, i) => (
        <li key={i} className="grid grid-cols-12 gap-6 py-4">
          <div className={`col-span-4 text-[15px] font-medium ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{it.title}</div>
          <div className={`col-span-8 text-[15px] ${isDark ? "text-white/60" : "text-black/55"}`}>{it.desc}</div>
        </li>
      ))}
    </ul>
  );
};

/**
 * Neutral, generously-spaced statement — used for the four-pillars overview
 * and other typographic scenes.
 */
export const SceneStatement = ({ eyebrow, children }) => {
  const { isDark } = useTheme();
  return (
    <div className="py-8">
      {eyebrow && <div className={`k-eyebrow ${isDark ? "text-white/50" : "text-black/45"}`}>{eyebrow}</div>}
      <div className="mt-4">{children}</div>
    </div>
  );
};
